import { Check, ChevronDown } from "lucide-react";

const ACCENTS = {
  amber: {
    ring: "focus-within:border-amber-500/70 focus-within:ring-2 focus-within:ring-amber-500/20",
    icon: "group-focus-within:text-amber-400",
    badge: "bg-amber-500/10 border-amber-500/40 text-amber-400",
    star: "text-amber-500",
    chip: "border-amber-500 bg-amber-500/15 text-amber-200",
    tile: "border-amber-500 bg-amber-500/10 text-amber-100 shadow-[0_10px_30px_-14px_rgba(245,158,11,0.7)]",
    tileIcon: "text-amber-400",
    bar: "from-amber-500 to-amber-300",
    text: "text-amber-400",
    rail: "from-amber-500/50",
  },
  violet: {
    ring: "focus-within:border-violet-500/70 focus-within:ring-2 focus-within:ring-violet-500/20",
    icon: "group-focus-within:text-violet-300",
    badge: "bg-violet-500/10 border-violet-500/40 text-violet-300",
    star: "text-violet-400",
    chip: "border-violet-500 bg-violet-500/15 text-violet-200",
    tile: "border-violet-500 bg-violet-500/10 text-violet-100 shadow-[0_10px_30px_-14px_rgba(139,92,246,0.7)]",
    tileIcon: "text-violet-300",
    bar: "from-violet-500 to-fuchsia-400",
    text: "text-violet-300",
    rail: "from-violet-500/50",
  },
};
const DONE_BADGE = "bg-emerald-500/15 border-emerald-500/50 text-emerald-400";

export const inputCls = "w-full bg-transparent h-12 pl-10 pr-10 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none rounded-xl";
export const areaCls = "w-full bg-transparent py-3 pl-10 pr-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none rounded-xl resize-y min-h-[112px] leading-relaxed";
export const selectCls = "w-full appearance-none bg-transparent h-12 pl-10 pr-10 text-sm text-zinc-100 focus:outline-none rounded-xl cursor-pointer [&>option]:bg-zinc-900";

export function FormSection({ n, title, hint, accent = "amber", done = false, last = false, testid, children }) {
  const a = ACCENTS[accent];
  return (
    <section data-testid={testid} className={`relative sm:pl-12 ${last ? "" : "pb-9"}`}>
      {!last && <span aria-hidden className={`hidden sm:block absolute left-4 top-11 bottom-3 w-px bg-gradient-to-b ${a.rail} to-transparent`} />}
      <div className="flex items-center gap-3 mb-5 sm:-ml-12">
        <span className={`w-8 h-8 rounded-lg border font-adult-mono text-xs font-bold flex items-center justify-center shrink-0 transition-colors duration-300 ${done ? DONE_BADGE : a.badge}`}>
          {done ? <Check className="w-4 h-4" /> : n}
        </span>
        <div className="min-w-0">
          <h3 className="font-adult-heading text-lg leading-tight text-white">{title}</h3>
          {hint && <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function Field({ label, htmlFor, required, optional, icon: Icon, filled, select, top, accent = "amber", className = "", children }) {
  const a = ACCENTS[accent];
  const pos = top ? "top-3.5" : "top-1/2 -translate-y-1/2";
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-400">
        {label}{required && <span className={a.star}>*</span>}
        {optional && <span className="ml-1 normal-case tracking-normal text-zinc-600">(optional)</span>}
      </label>
      <div className={`group relative mt-1.5 rounded-xl border border-zinc-800 bg-zinc-950/70 transition-[border-color,box-shadow] duration-200 hover:border-zinc-700 ${a.ring}`}>
        {Icon && <Icon className={`absolute left-3.5 ${pos} w-4 h-4 text-zinc-500 pointer-events-none transition-colors ${a.icon}`} />}
        {children}
        {select && <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />}
        {!select && filled && <Check className={`absolute right-3.5 ${pos} w-4 h-4 text-emerald-400 pointer-events-none animate-in zoom-in-50 fade-in duration-300`} />}
      </div>
    </div>
  );
}

export function Tile({ on, onClick, testid, accent = "amber", icon: Icon, title, sub, className = "" }) {
  const a = ACCENTS[accent];
  return (
    <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
      className={`relative rounded-xl border p-3.5 text-left transition-[border-color,background-color,box-shadow,transform] duration-200 active:scale-[0.98] ${on ? a.tile : "border-zinc-800 bg-zinc-950/40 text-zinc-300 hover:border-zinc-600 hover:-translate-y-0.5"} ${className}`}>
      {on && <Check className={`absolute top-2.5 right-2.5 w-3.5 h-3.5 ${a.tileIcon}`} />}
      {Icon && <Icon className={`w-5 h-5 mb-2 transition-colors ${on ? a.tileIcon : "text-zinc-500"}`} />}
      <div className="text-sm font-medium leading-tight pr-4">{title}</div>
      {sub && <div className="mt-1 text-[11px] text-zinc-500 font-adult-mono">{sub}</div>}
    </button>
  );
}

export function Chip({ on, onClick, testid, accent = "amber", children }) {
  const a = ACCENTS[accent];
  return (
    <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-medium transition-[background-color,border-color,color,transform] duration-200 active:scale-95 ${on ? a.chip : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"}`}>
      {on && <Check className="w-3 h-3" />}{children}
    </button>
  );
}

export function FormProgress({ done, total, accent = "amber" }) {
  const a = ACCENTS[accent];
  const pct = Math.round((done / total) * 100);
  return (
    <div data-testid="form-progress" className="mb-8">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">
        <span>{done >= total ? "All set" : `${done} of ${total} done`}</span>
        <span className={a.text}>{pct}%</span>
      </div>
      <div className="mt-2 h-1 rounded-full bg-zinc-800/80 overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${a.bar} transition-[width] duration-500 ease-out`} style={{ width: `${Math.max(pct, 4)}%` }} />
      </div>
    </div>
  );
}
